import React, { useState, useEffect } from 'react';
import ApiClient from '../../utils/ApiClient';
import { ClockIcon } from '@heroicons/react/20/solid';

export default function RecentTrackingList({ onTrackingSelect }) {
    const [trackingNumbers, setTrackingNumbers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchRecentTrackingNumbers();
    }, []);

    const fetchRecentTrackingNumbers = async () => {
        try {
            setLoading(true);
            const response = await ApiClient.get('/api/recentremovaltrackingnumbers?limit=10');

            if (response.status === 204) {
                // No content - empty list
                setTrackingNumbers([]);
            } else {
                setTrackingNumbers(response.data || []);
            }
            setError(null);
        } catch (err) {
            console.error('Error fetching recent tracking numbers:', err);
            setError('Failed to load recent tracking numbers');
            setTrackingNumbers([]);
        } finally {
            setLoading(false);
        }
    };

    const handleTrackingClick = (tracking) => {
        if (onTrackingSelect) {
            onTrackingSelect(tracking);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';

        try {
            const date = new Date(dateString);
            const now = new Date();
            const diffMs = now - date;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);

            if (diffMins < 60) {
                return `${diffMins}m ago`;
            } else if (diffHours < 24) {
                return `${diffHours}h ago`;
            } else if (diffDays < 7) {
                return `${diffDays}d ago`;
            } else {
                return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            }
        } catch (error) {
            return 'N/A';
        }
    };

    if (loading) {
        return (
            <div className="mt-6 w-[400px]">
                <div className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                    <ClockIcon className="h-4 w-4 mr-1.5 text-gray-500" />
                    Recent Tracking Numbers
                </div>
                <div className="bg-white rounded-md shadow-sm border border-gray-200 p-4">
                    <p className="text-sm text-gray-500">Loading...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mt-6 w-[400px]">
                <div className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                    <ClockIcon className="h-4 w-4 mr-1.5 text-gray-500" />
                    Recent Tracking Numbers
                </div>
                <div className="bg-white rounded-md shadow-sm border border-red-200 p-4">
                    <p className="text-sm text-red-600">{error}</p>
                </div>
            </div>
        );
    }

    if (trackingNumbers.length === 0) {
        return (
            <div className="mt-6 w-[400px]">
                <div className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                    <ClockIcon className="h-4 w-4 mr-1.5 text-gray-500" />
                    Recent Tracking Numbers
                </div>
                <div className="bg-white rounded-md shadow-sm border border-gray-200 p-4">
                    <p className="text-sm text-gray-500">No recent tracking numbers found</p>
                </div>
            </div>
        );
    }

    return (
        <div className="mt-6">
            <div className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                <ClockIcon className="h-4 w-4 mr-1.5 text-gray-500" />
                Recent Tracking Numbers
            </div>
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                <div className="max-h-80 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-300">
                        <thead className="bg-gray-50 sticky top-0">
                            <tr>
                                <th scope="col" className="py-3 pl-4 pr-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 sm:pl-6">
                                    Tracking Number
                                </th>
                                <th scope="col" className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                    Order ID
                                </th>
                                <th scope="col" className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                    Item Name / ASIN
                                </th>
                                <th scope="col" className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                    Disposition
                                </th>
                                <th scope="col" className="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">
                                    SKUs
                                </th>
                                <th scope="col" className="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">
                                    Qty
                                </th>
                                <th scope="col" className="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">
                                    Pending
                                </th>
                                <th scope="col" className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                    Time
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                            {trackingNumbers.map((item, index) => (
                                <tr
                                    key={item.tracking}
                                    onClick={() => handleTrackingClick(item.tracking)}
                                    className="hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                                >
                                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                        {item.tracking}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                        {item.order_id || '-'}
                                    </td>
                                    <td className="px-3 py-4 text-sm text-gray-500 max-w-xs">
                                        <div className="truncate font-medium">{item.item_name || '-'}</div>
                                        {item.asin1 && (
                                            <div className="text-xs text-gray-400 truncate">{item.asin1}</div>
                                        )}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                        {item.disposition || '-'}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                                        {item.item_count || '-'}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                                        {item.total_quantity || '-'}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                            item.pending > 0 ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                                        }`}>
                                            {item.pending || 0}
                                        </span>
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-400">
                                        {formatDate(item.created_at)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
